/**
 * saved-file-ui.ts — local save and open (P1 US-117, FR-29, goal G5).
 *
 * Ported from oracle lines 1199–1226: `sauver` and `charger`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * BOTH DIRECTIONS HAVE TO KEEP WORKING, AND THAT IS AN EXIT CRITERION
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * A file written by today's HTML tool must open in the beta, and a file written by the beta must
 * open in today's HTML tool. The second direction is the one that is easy to forget and the one
 * that matters during the transition: the original file stays in production use until the P1 exit
 * gate, so for a period the same engineer is moving files between the two. Any field the beta adds
 * to the format breaks that, which is why this file writes exactly the frozen v1 shape and nothing
 * else.
 *
 * One deliberate difference from the oracle: reading goes through the contract's `parseSavedFile`
 * rather than a bare `JSON.parse`. The oracle's failure mode was
 * `alert('Fichier illisible : ' + e.message)` — a browser dialog carrying a parser's message,
 * which tells a user nothing they can act on.
 */

import {
  DEFAULT_SAVED_MODULE,
  SAVED_FILE_FORMAT,
  SAVED_FILE_MODULES,
  SAVED_FILE_SCHEMA_VERSION,
  parseSavedFile,
  type NormalisedSavedFile,
  type SavedModule,
} from '@ronceray/contract';

/**
 * Build the saved-file payload.
 *
 * **All three modules are written, whatever tab is showing.** That is the oracle's behaviour and
 * it is what makes a saved file a project rather than a snapshot of one pane — and it is why
 * `invalid-cases/README.md` notes that one saved file yields up to three importable cases for the
 * P2 importer, not one.
 *
 * `format` is a legacy constant, kept for compatibility and never used for version detection;
 * `schemaVersion` is authoritative. Both come from the contract rather than being typed here.
 */
export function buildSavedFile(
  projet: string,
  onglet: SavedModule,
  read: (mod: SavedModule) => Record<string, unknown>,
): Record<string, unknown> {
  const cas: Record<string, unknown> = {};
  for (const mod of SAVED_FILE_MODULES) cas[mod] = read(mod);

  // Key order matches the oracle's object literal so a diff between a file written by the two
  // tools is empty rather than merely equivalent.
  return {
    schemaVersion: SAVED_FILE_SCHEMA_VERSION,
    format: SAVED_FILE_FORMAT,
    projet,
    onglet,
    cas,
  };
}

/**
 * The download filename. Oracle line 1206.
 *
 * The element label, with everything outside `[A-Za-z0-9_-]` collapsed to underscores, falling
 * back to `calcul`. Deliberately aggressive: the label is free text an engineer typed, and it ends
 * up as a filename on Windows, macOS and Linux.
 */
export function fileName(repere: unknown): string {
  const base = typeof repere === 'string' && repere.length > 0 ? repere : 'calcul';
  return `${base.replace(/[^A-Za-z0-9_-]+/g, '_')}.json`;
}

/** Serialise exactly as the oracle does — two-space indent, so a saved file stays diffable. */
export function serialiseSavedFile(data: Record<string, unknown>): string {
  return JSON.stringify(data, null, 2);
}

/** Trigger the browser download. Oracle lines 1204–1208. */
export function downloadSavedFile(data: Record<string, unknown>, repere: unknown): void {
  const blob = new Blob([serialiseSavedFile(data)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName(repere);
  a.click();
  URL.revokeObjectURL(a.href);
}

/** What a successful open yields, or why it failed. */
export type OpenResult =
  | { readonly ok: true; readonly file: NormalisedSavedFile }
  | { readonly ok: false; readonly message: string };

/**
 * Parse a file's text through the contract.
 *
 * A **v0 file — no `schemaVersion` — opens silently**, as the contract specifies. Those are the
 * internal files written before the US-002 patch; there is no user-visible difference, and there
 * must not be one, because a warning would tell an engineer their perfectly good file is somehow
 * suspect.
 *
 * A file the contract rejects produces a readable sentence naming the problem, rather than the
 * oracle's `alert('Fichier illisible : ' + e.message)`.
 */
export function openSavedFile(text: string): OpenResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, message: 'Ce fichier n est pas du JSON valide.' };
  }

  const parsed = parseSavedFile(raw);

  if (parsed.ok) return { ok: true, file: parsed.file };

  // Two failures with different meanings, and they get different sentences. "This file is newer
  // than this build" is something the user can act on — update, or ask someone to — while "this
  // file is corrupt" is not, and telling them the wrong one wastes their time.
  if (parsed.reason === 'unsupported-version') {
    return {
      ok: false,
      message: `Ce fichier a ete ecrit par une version plus recente (schemaVersion ${parsed.version}).`,
    };
  }

  const first = parsed.error.issues[0];
  const where = first?.path.join('.') ?? '';
  return {
    ok: false,
    message:
      where.length > 0
        ? `Fichier de cas invalide : le champ "${where}" n est pas exploitable.`
        : 'Fichier de cas invalide : le format n est pas reconnu.',
  };
}

/** Which tab to restore. Falls back to the contract's default rather than leaving it undefined. */
export function tabToRestore(file: NormalisedSavedFile): SavedModule {
  return file.onglet ?? DEFAULT_SAVED_MODULE;
}
